import type { Table } from '@tanstack/react-table';
import { XIcon } from 'lucide-react';
import {
  type ComponentProps,
  type CSSProperties,
  ReactNode,
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import { createPortal } from 'react-dom';

import { Button } from '@/components/core/button';
import { Kbd } from '@/components/core/kbd';
import { Separator } from '@/components/core/separator';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/core/tooltip';
import { cn } from '@/shared/helpers';
import { useDebouncedValue } from '@/shared/hooks/use-debounced-value';

function DataTableActionMenuSelection({
  onClear,
  selectedCount,
}: {
  onClear: () => void;
  selectedCount: number;
}) {
  return (
    <div className="flex h-7 items-center rounded-md border border-invert-border pr-1 pl-2.5">
      <span className="text-xs whitespace-nowrap">{selectedCount} selected</span>
      <Separator
        orientation="vertical"
        className="mr-1 ml-2 bg-invert-border data-[orientation=vertical]:h-4"
      />
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant="ghost"
            size="icon-xs"
            className="hover:bg-invert-accent hover:text-invert-accent-foreground"
            onClick={onClear}
            aria-label="Clear selection"
          >
            <XIcon className="size-3.5" />
          </Button>
        </TooltipTrigger>
        <TooltipContent sideOffset={10} className="flex items-center gap-2 px-2 py-1">
          Clear selection
          <Kbd>Esc</Kbd>
        </TooltipContent>
      </Tooltip>
    </div>
  );
}

type DataTableActionMenuProps<TData> = Omit<ComponentProps<'div'>, 'children'> & {
  table: Table<TData>;
  visible?: boolean;
  container?: Element | DocumentFragment | null;
  children?: ReactNode | ((selectedRows: TData[]) => ReactNode);
};

const ANIMATION_MS = 200;

function DataTableActionMenu<TData>({
  table,
  visible: visibleProp,
  container: containerProp,
  children,
  className,
  ...props
}: DataTableActionMenuProps<TData>) {
  // rowSelection tracks all selected IDs including those not within table's row data. However, Due to manual pagination
  // the row data will only contain the current page. To give actions access to all selected rows we need to cache them
  // here when they are available.
  const { rowSelection } = table.getState();
  const { rowsById } = table.getRowModel();
  const selectedRowCache = useRef(new Map<string, TData>());
  const selectedRows = useMemo(() => {
    const selected: TData[] = [];
    Object.entries(rowSelection).forEach(([id, isSelected]) => {
      if (isSelected) {
        // always try to use the latest version from the table
        let row: TData | undefined = rowsById[id]?.original;
        if (row) {
          selectedRowCache.current.set(id, row);
        } else {
          row = selectedRowCache.current.get(id);
        }
        if (row) {
          selected.push(row);
        }
      } else {
        selectedRowCache.current.delete(id);
      }
    });
    return selected;
  }, [rowSelection, rowsById, selectedRowCache]);

  const clearSelection = useCallback(() => {
    table.resetRowSelection(true);
  }, [table]);

  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        clearSelection();
      }
    }

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [clearSelection]);

  const container = containerProp ?? globalThis.document?.body;
  const isOpen = visibleProp ?? selectedRows.length > 0;

  // Render immediately when open, otherwise debounce this state until just before close animation finishes.
  const isRendering = useDebouncedValue(isOpen, ANIMATION_MS - 10) || isOpen;

  if (!container) return null;
  return createPortal(
    isRendering ? (
      <div
        role="toolbar"
        aria-orientation="horizontal"
        data-state={isOpen ? 'open' : 'closed'}
        style={{ ...props.style, '--animation-duration': `${ANIMATION_MS}ms` } as CSSProperties}
        className={cn(
          'fixed inset-x-0 bottom-9 z-50 mx-auto flex w-fit flex-wrap items-center justify-center gap-2 rounded-md border border-invert-border bg-invert p-2 text-invert-foreground shadow-sm',
          'duration-(--animation-duration) fade-in fade-out slide-in-from-bottom-2 slide-out-to-bottom-2 data-[state=closed]:animate-out data-[state=open]:animate-in',
          className,
        )}
        {...props}
      >
        <DataTableActionMenuSelection
          onClear={clearSelection}
          selectedCount={selectedRows.length}
        />
        <Separator orientation="vertical" className="hidden h-5! bg-invert-border sm:block" />
        {typeof children === 'function' ? children(selectedRows) : children}
      </div>
    ) : null,
    container,
  );
}

const DataTableAction = forwardRef<HTMLButtonElement, ComponentProps<typeof Button>>(
  ({ size = 'sm', className, children, ...props }, ref) => (
    <Button
      ref={ref}
      variant="secondary"
      size={size}
      className={cn(
        'gap-1.5 bg-invert-secondary text-invert-secondary-foreground hover:bg-invert-secondary/50 [&>svg]:size-3.5',
        size === 'icon' ? 'size-7' : 'h-7',
        className,
      )}
      {...props}
    >
      {children}
    </Button>
  ),
);

DataTableAction.displayName = 'DataTableAction';

export { DataTableActionMenu, DataTableAction };
